import React, { useEffect, useState } from "react";
import { Box, Flex, Select, Input, IconButton, Text, Tooltip } from "@chakra-ui/react";
import { EditIcon } from "@chakra-ui/icons";
import axios from "axios";
import { useSupplierRetailer } from "../../wrappers/SupplierRetailerContext";
import TableWeekly from "./components/TableWeekly";
import TableMonthly from "./components/TableMonthly";
import TableReason from "./components/TableReason";
import TableComment from "./components/TableComment";
import EditStrikeRate from "./components/EditStrikeRate";
import Exports from "./components/Exports";
import PageToolbar from "../../components/shared/PageToolbar";
import { useAuthentication } from "../../wrappers/UseAuthentication";
import { neutral, type, radius, elevation } from "../../theme/tokens";

// A titled panel. Previously each of these wore a solid teal bar with white
// text plus a teal gradient hairline on top — four of them on one screen.
const DashboardCard = ({ title, children, flex, ...rest }) => (
  <Box
    flex={flex}
    minW={0}
    bg={neutral[0]}
    borderRadius={radius.md}
    border="1px solid"
    borderColor={neutral[200]}
    boxShadow={elevation.raised}
    overflow="hidden"
    {...rest}
  >
    {title && (
      <Box px={3} py={2} borderBottom="1px solid" borderColor={neutral[200]} bg={neutral[50]}>
        <Text
          fontSize={type.size.micro}
          fontWeight={type.weight.bold}
          letterSpacing={type.tracking.label}
          textTransform="uppercase"
          color={neutral[600]}
        >
          {title}
        </Text>
      </Box>
    )}
    <Box p={2}>{children}</Box>
  </Box>
);

const StrikeRate = () => {
  const [ddStrikeRate, setDdStrikeRate] = useState([]);
  const [weekEndingDates, setWeekEndingDates] = useState([]);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const { user_type } = useAuthentication();
  const { selectedRetailer, selectedSupplier } = useSupplierRetailer();
  const [loading, setLoading] = useState(false);

  const [weekEndingDate, setWeekEndingDate] = useState("");
  const [comment, setComment] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchCommitted, setSearchCommitted] = useState("");


  useEffect(() => {
    setLoading(true);
    setWeekEndingDate(""); // reset so a stale week never survives a retailer/supplier switch
    setWeekEndingDates([]);
    loadData();
  }, [selectedRetailer, selectedSupplier]);

  useEffect(() => {
    if (weekEndingDates.length > 0 && !weekEndingDate) {
      setWeekEndingDate(weekEndingDates[0].week_ending_date);
    }
  }, [weekEndingDates]);

  // These two share no data, so serialising them cost a full ~0.7s SA->US round trip
  // for nothing. They now go out together.
  const loadData = async () => {
    await Promise.all([fetchDdStrikeRate(), fetchWeekEndingDates()]);
    setLoading(false);
  };

  // TableComment asks the server for the latest week when it has none yet, and reports
  // back which week that turned out to be. Adopting it here keeps the dropdown showing
  // the week actually on screen. `prev ||` means a week the user already picked wins,
  // and TableComment keys its refetch on the resolved week, so this never causes a
  // second request for data we already have.
  const handleResolvedWeek = (resolved) => {
    setWeekEndingDate((prev) => prev || resolved || "");
  };

  const fetchDdStrikeRate = async () => {
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/rep_strikeRate/get-dd-strike-rate`, {});
      setDdStrikeRate(res.data.data);
    } catch (error) {
      console.error("Error fetching strike rate data:", error);
    }
  };

  const fetchWeekEndingDates = async () => {
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/rep_strikeRate/week-ending-date`, {
        retailer: selectedRetailer,
      });
      setWeekEndingDates(res.data.data);
    } catch (error) {
      console.error("Error fetching week ending dates:", error);
    }
  };

  const handleToggleEdit = () => {
    setIsEditOpen(!isEditOpen);
  };

  function formatDateString(dateString) {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const date = new Date(dateString);
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear().toString().slice(-2);
    return `${day} ${month} '${year}`;
  }

  return (
    <Box>
      <PageToolbar
        filters={
          <>
            <Select
              aria-label="Week ending"
              maxW="170px"
              value={weekEndingDate}
              onChange={(e) => setWeekEndingDate(e.target.value)}
            >
              {weekEndingDates.map((date, index) => (
                <option key={index} value={date.week_ending_date}>
                  Week ending {formatDateString(date.week_ending_date)}
                </option>
              ))}
            </Select>
            <Select
              aria-label="Reason"
              maxW="190px"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            >
              <option value="">All reasons</option>
              {ddStrikeRate.map((item) => (
                <option key={item.uuid} value={item.uuid}>
                  {item.text}
                </option>
              ))}
            </Select>
            <Input
              maxW="200px"
              placeholder="Search article or store"
              aria-label="Search strike rate lines"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") setSearchCommitted(searchInput);
              }}
            />
          </>
        }
        actions={
          <Exports
            supplier={selectedSupplier}
            retailer={selectedRetailer}
            week_ending_date={weekEndingDate}
            dd_uuid={comment}
          />
        }
      />

      {/* Summary Tables Row */}
      <Flex gap={4} mb={4} align="stretch" direction={{ base: "column", lg: "row" }}>
        <DashboardCard title="Weekly" flex="1">
          <TableWeekly selectedRetailer={selectedRetailer} selectedSupplier={selectedSupplier} />
        </DashboardCard>
        <DashboardCard title="Monthly" flex="1">
          <TableMonthly selectedRetailer={selectedRetailer} selectedSupplier={selectedSupplier} />
        </DashboardCard>
        <DashboardCard title="By reason" flex="1.5">
          <TableReason
            selectedRetailer={selectedRetailer}
            selectedSupplier={selectedSupplier}
            ddStrikeRate={ddStrikeRate}
          />
        </DashboardCard>
      </Flex>

      {/* Comment Detail Table */}
      <DashboardCard title="Line detail">
        {/* Mounted immediately rather than gated on weekEndingDate. Waiting for the week
            dropdown put this — the largest table on the page — third in a chain of round
            trips; it now loads alongside the summary tables. */}
        {!loading && weekEndingDates.length === 0 ? (
          <Text fontSize={type.size.small} color={neutral[500]} p={3}>
            {`No strike rate data for ${selectedRetailer}`}
          </Text>
        ) : (
          <TableComment
            selectedRetailer={selectedRetailer}
            selectedSupplier={selectedSupplier}
            ddStrikeRate={ddStrikeRate}
            weekEndingDate={weekEndingDate}
            comment={comment}
            search={searchCommitted}
            onResolvedWeek={handleResolvedWeek}
          />
        )}
      </DashboardCard>

      {/* Edit Popover */}
      {isEditOpen && (
        <EditStrikeRate
          isOpen={isEditOpen}
          onClose={handleToggleEdit}
          selectedRetailer={selectedRetailer}
          selectedSupplier={selectedSupplier}
          onDataSaved={loadData}
        />
      )}

      {/* Admin Edit Button */}
      {user_type === "admin" && (
        <Tooltip label="Edit strike rate data" placement="left">
          <IconButton
            icon={<EditIcon />}
            aria-label="Edit strike rate data"
            position="fixed"
            bottom="24px"
            right="24px"
            size="md"
            isRound
            boxShadow={elevation.overlay}
            onClick={handleToggleEdit}
            zIndex={800}
          />
        </Tooltip>
      )}
    </Box>
  );
};

export default StrikeRate;
